import styled from '@emotion/styled';
import { useSidebarStore } from '@shared/hooks/state/SidebarContainer';
import { getCorrectedHref } from '@shared/hooks/useActiveNavLink';
import { IMenuItem } from '@shared/models/components/IMenuProps';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import Icons from '../images/Icons';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { TextSize100 } from '../text/Typography';
import { MixinMenuCollapseHide, MixinMenuItemRow, MixinMenuMediaHide } from './CommonMenuStyles';
import MenuItem from './MenuItem';
import SubmenuItem from './SubmenuItem';

interface IMenuSidebarProps {
    prefixComponent?: React.ReactNode;
    topMenuItems: IMenuItem[];
    bottomMenuItems: IMenuItem[];
}

export default function MenuSidebar({ prefixComponent, topMenuItems, bottomMenuItems }: IMenuSidebarProps) {
    const { t } = useTranslation('common');
    const location = useLocation();
    const isCollapsed = useSidebarStore((state) => state.isCollapsed);
    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
    const [manualToggles, setManualToggles] = useState<Record<string, boolean>>({});

    // Automatically open menus based on location.pathname, respecting manual toggles
    useEffect(() => {
        const updatedOpenMenus: Record<string, boolean> = { ...openMenus };
        [...topMenuItems, ...bottomMenuItems].forEach((item) => {
            if (item.submenu) {
                const correctedHref = getCorrectedHref(item.href);
                if (location.pathname.startsWith(correctedHref)) {
                    updatedOpenMenus[item.label] = true;
                    manualToggles[item.label] = true;
                }
            }
        });
        setOpenMenus(updatedOpenMenus);
    }, [location.pathname, topMenuItems, bottomMenuItems]);

    const toggleMenu = (label: string) => {
        setManualToggles((prevState) => ({
            ...prevState,
            [label]: !prevState[label],
        }));
        setOpenMenus((prevState) => ({
            ...prevState,
            [label]: !prevState[label],
        }));
    };

    const checkActiveSub = (item: IMenuItem) => {
        return openMenus[item.label] || false;
    };

    const renderMenuItems = (menuItems: IMenuItem[]) => {
        return menuItems.map((item, index) => (
            <MenuItemWrapper key={index} role="none">
                <MenuItemContainer>
                    <MenuItem item={item} />

                    {item.submenu && !item.activeSub && (
                        <ExpandButton
                            role="menuitem"
                            aria-expanded={checkActiveSub(item)}
                            aria-haspopup="true"
                            aria-label={t('menu.expand')}
                            onClick={() => toggleMenu(item.label)}>
                            <Icons name="expandArrow" size={8} />
                        </ExpandButton>
                    )}
                </MenuItemContainer>

                {item.submenu && (
                    <Submenu role="menu" isOpen={checkActiveSub(item)}>
                        {item.submenu.map((subItem, subIndex) => (
                            <SubmenuItem key={subIndex} subItem={subItem} />
                        ))}
                    </Submenu>
                )}
            </MenuItemWrapper>
        ));
    };

    return (
        <SidebarNav aria-label="Sidebar Navigation" data-collapsed={isCollapsed}>
            <TopSection>
                {prefixComponent}
                <MenuList role="menubar">{renderMenuItems(topMenuItems)}</MenuList>
            </TopSection>
            <BottomSection>
                <MenuList role="menubar">{renderMenuItems(bottomMenuItems)}</MenuList>
            </BottomSection>
        </SidebarNav>
    );
}

// Styled Components

const TopSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: scroll;
    scrollbar-width: none;
    margin-bottom: auto;
`;

const BottomSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-top: 1px solid ${(props) => props.theme.color.sidebarDivider};
`;

const SidebarNav = styled.nav`
    max-width: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-right: 1px solid ${(props) => props.theme.color.sidebarDivider};
`;

const MenuList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const MenuItemWrapper = styled.li``;

const MenuItemContainer = styled.div`
    ${(props) => MixinMenuItemRow(props.theme)}
`;

const ExpandButton = styled.button`
    margin-left: auto;
    height: 30px;
    min-width: 30px;
    background: none;
    border: none;
    cursor: pointer;
    ${TextSize100}
    color: inherit;
    &:hover,
    &:focus {
        outline: none;
    }

    transform: rotate(0deg);
    transition: transform 0.3s ease-in-out;

    &[aria-expanded='true'] {
        transform: rotate(180deg);
        ${MixinFocusVisible}
    }
    ${MixinMenuMediaHide}
    ${MixinMenuCollapseHide}
`;

const Submenu = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: ${(props: { isOpen: boolean }) => (props.isOpen ? 'block' : 'none')};
    padding-bottom: 20px;
    ${MixinMenuMediaHide}
    ${MixinMenuCollapseHide}
`;
